import React from 'react';
import {
  Box,
  Typography,
  Popover,
  alpha,
  Chip,
  List,
  ListItem,
  Paper,
} from '@mui/material';
import { changelog, changeTypes } from '../../data/changelog';

const Changelog = ({ anchorEl, onClose }) => {
  const open = Boolean(anchorEl);

  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            mt: 1.5,
            maxWidth: 450,
            maxHeight: '85vh',
            width: '100%',
            borderRadius: '20px',
            background: theme => theme.palette.mode === 'dark' 
              ? `
                radial-gradient(
                  ellipse at top,
                  ${alpha(theme.palette.background.default, 0.85)} 0%,
                  ${alpha(theme.palette.background.default, 0.9)} 50%,
                  ${alpha(theme.palette.background.default, 0.95)} 100%
                )
              `
              : `
                radial-gradient(
                  ellipse at top,
                  ${alpha(theme.palette.common.white, 0.85)} 0%,
                  ${alpha(theme.palette.common.white, 0.9)} 50%,
                  ${alpha(theme.palette.common.white, 0.95)} 100%
                )
              `,
            backdropFilter: 'blur(20px) saturate(1.8)',
            WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
            boxShadow: theme => `
              0 8px 32px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.6 : 0.15)},
              0 2px 8px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.1)},
              inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.1 : 0.2)}
            `,
            border: '1px solid',
            borderColor: theme => alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.2 : 0.1),
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            '&::before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              left: '50%',
              width: 10,
              height: 10,
              background: 'inherit',
              backdropFilter: 'inherit',
              WebkitBackdropFilter: 'inherit',
              transform: 'translate(-50%, -50%) rotate(45deg)',
              borderLeft: '1px solid',
              borderTop: '1px solid',
              borderColor: 'inherit',
              zIndex: 1,
            },
          }
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid',
        borderColor: theme => alpha(theme.palette.divider, 0.1),
        position: 'sticky',
        top: 0,
        background: 'transparent',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 2,
        borderRadius: '20px 20px 0 0',
        textAlign: 'center',
      }}>
        <Typography variant="h5" sx={{ 
          fontFamily: 'StrawberrySansDisplay',
          fontWeight: 500,
          color: 'text.primary',
          mb: 0,
          fontSize: '1.5rem',
        }}>
          Changelog
        </Typography>
      </Box>

      {/* Scrollable Cards Container */}
      <Box sx={{ 
        overflow: 'auto',
        flexGrow: 1,
        position: 'relative',
        borderRadius: '0 0 20px 20px',
        background: 'transparent',
        borderTop: 'none',
        '&::-webkit-scrollbar': {
          width: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: theme => alpha(theme.palette.primary.main, 0.15),
          borderRadius: '20px',
          border: '1px solid transparent',
          backgroundClip: 'padding-box',
          '&:hover': {
            background: theme => alpha(theme.palette.primary.main, 0.25),
          }
        },
        scrollbarWidth: 'thin',
        scrollbarColor: theme => `${alpha(theme.palette.primary.main, 0.15)} transparent`,
      }}>
        {/* Changelog Cards */}
        <Box sx={{ 
          position: 'relative',
          pt: 2,
          pb: 2,
        }}>
          {changelog.map((entry, index) => (
            <Box
              key={entry.version}
              sx={{
                position: 'relative',
                p: 0,
                mb: 1.5,
                mx: 2.5,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  p: 3,
                  transition: 'all 0.2s ease',
                  border: '1px solid',
                  borderColor: theme => alpha(theme.palette.divider, 0.2),
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`,
                  '&:hover': {
                    bgcolor: theme => alpha(theme.palette.primary.main, 0.03),
                    borderColor: theme => alpha(theme.palette.primary.main, 0.15),
                    transform: 'translateY(-2px)',
                    boxShadow: theme => `
                      0 8px 24px ${alpha(theme.palette.common.black, 0.12)},
                      0 2px 8px ${alpha(theme.palette.primary.main, 0.08)}
                    `,
                  }
                }}
              >

                {/* Content */}
                <Box sx={{ pl: 0 }}>
                {/* Version info row */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  mb: 2.5,
                  flexWrap: 'wrap',
                }}>
                  <Chip
                    label={`v${entry.version}`}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      fontFamily: 'StrawberrySansText',
                      bgcolor: theme => alpha(changeTypes[entry.type].color, 0.1),
                      color: changeTypes[entry.type].color,
                      border: '1px solid',
                      borderRadius: 2,
                      borderColor: theme => alpha(changeTypes[entry.type].color, 0.25),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: theme => alpha(changeTypes[entry.type].color, 0.15),
                        transform: 'translateY(-1px)',
                      }
                    }}
                  />
                  <Chip
                    label={changeTypes[entry.type].label}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      fontFamily: 'StrawberrySansText',
                      bgcolor: theme => alpha(changeTypes[entry.type].color, 0.1),
                      color: changeTypes[entry.type].color,
                      border: '1px solid',
                      borderRadius: 2,
                      borderColor: theme => alpha(changeTypes[entry.type].color, 0.25),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: theme => alpha(changeTypes[entry.type].color, 0.15),
                        transform: 'translateY(-1px)',
                      }
                    }}
                  />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      ml: 'auto',
                      opacity: 0.8,
                      fontWeight: 500,
                      fontFamily: 'StrawberrySansText',
                      bgcolor: theme => alpha(theme.palette.background.default, 0.5),
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: theme => alpha(theme.palette.divider, 0.3),
                    }}
                  >
                    {new Date(entry.date).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>

                {/* Description */}
                {entry.description && (
                  <Box
                    sx={{
                      mb: 2,
                      position: 'relative',
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.primary',
                        lineHeight: 1.6,
                        fontWeight: 500,
                        fontFamily: 'StrawberrySansText',
                        p: 2,
                        backgroundColor: theme => alpha(changeTypes[entry.type].color, 0.05),
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: theme => alpha(changeTypes[entry.type].color, 0.1),
                        borderLeft: '3px solid',
                        borderLeftColor: changeTypes[entry.type].color,
                      }}
                    >
                      {entry.description}
                    </Typography>
                  </Box>
                )}

                <List sx={{ 
                  mb: 0,
                  pt: 0,
                  pb: 0,
                  '& li': {
                    position: 'relative',
                    pl: 2.5,
                    py: 0.5,
                    margin: 0,
                    minHeight: 'unset',
                    '&::before': {
                      content: '"•"',
                      position: 'absolute',
                      left: 0.5,
                      top: '0.5rem',
                      color: theme => alpha(changeTypes[entry.type].color, 0.7),
                      fontSize: '1rem',
                      lineHeight: 1,
                      fontWeight: 'bold',
                    },
                    '&:hover': {
                      bgcolor: theme => alpha(changeTypes[entry.type].color, 0.02),
                      borderRadius: 1,
                      '&::before': {
                        color: changeTypes[entry.type].color,
                      }
                    }
                  }
                }}>
                  {entry.changes.map((change, changeIndex) => (
                    <ListItem key={changeIndex} sx={{ 
                      display: 'list-item',
                      p: 0,
                      pb: 1,
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.6,
                          fontSize: '0.875rem',
                          fontFamily: 'StrawberrySansText',
                          opacity: 0.9,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            opacity: 1,
                            color: 'text.primary',
                          }
                        }}
                      >
                        {change}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                </Box>
              </Paper>
            </Box>
          ))}
        </Box>
      </Box>
    </Popover>
  );
};

export default Changelog; 